import { useMediaQuery } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../../../App.styles';
import articlePlaceholder from '../../../../../assets/articlePlaceholder.png';
import { Chip, Typography } from '../../../../../stories/atoms';
import {
  ColumnFlexContainer,
  Divider,
  RowFlexContainer,
} from '../../../../../stories/atoms/containerV2/ContainerV2';
import { muiTheme } from '../../../../../theme';
import { soldProductsTranslations, storeOverviewTranslations } from '../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../utils';
import { getArticleAssetUrl } from '../../../../../utils/soldProducts';
import { MobileChipLayout } from '../mobileChipLayout/MobileChipLayout';
import { StyledIndexTypography } from '../mobileChipLayout/MobileChipLayout.styles';
import {
  getDifferenceText,
  getRankStatus,
  PlanProductItemRankedAreaSalesMarket,
  SoldProductItemRankedAreaSalesMarket,
} from '../sales.util';
import { StyledProductImage } from './ProductSummaryCard.styles';

type ProductSummaryCardProps = {
  data: SoldProductItemRankedAreaSalesMarket | PlanProductItemRankedAreaSalesMarket;
  onClick: (data: SoldProductItemRankedAreaSalesMarket | PlanProductItemRankedAreaSalesMarket) => void;
};

type ProductSummarySliderProps = {
  data: SoldProductItemRankedAreaSalesMarket | PlanProductItemRankedAreaSalesMarket | null;
};

export const ProductSummaryCard: FC<ProductSummaryCardProps> = ({ data, onClick }) => {
  const { departmentNumber, productId, productName, differenceLastLastWeek, rankingVsArea, rankingVsSalesMarket } =
    data;

  const sendCardDetails = () => {
    onClick(data);
  };

  return (
    <ColumnFlexContainer
      borderRadius={[2]}
      padding={[3, 3]}
      gap={[1]}
      cursor="pointer"
      backgroundColor="backgroundSelectedDark"
      onClick={sendCardDetails}
    >
      <RowFlexContainer justifyContent="between" alignItems="center">
        <Typography type="button">{productName}</Typography>
        <Typography>{getDifferenceText(differenceLastLastWeek, true)}</Typography>
      </RowFlexContainer>
      <SecondaryTextTypography type="caption">{productId}</SecondaryTextTypography>
      <RowFlexContainer justifyContent="between" alignItems="center">
        <Typography type="caption">Dept: {departmentNumber}</Typography>
        <RowFlexContainer gap={[1]}>
          <StyledIndexTypography type="caption" status={getRankStatus(rankingVsArea)}>
            vs Area: {rankingVsArea}
          </StyledIndexTypography>
          <SecondaryTextTypography type="caption">/</SecondaryTextTypography>
          <StyledIndexTypography type="caption" status={getRankStatus(rankingVsSalesMarket)}>
            vs SM: {rankingVsSalesMarket}
          </StyledIndexTypography>
        </RowFlexContainer>
      </RowFlexContainer>
    </ColumnFlexContainer>
  );
};

export const ProductSummarySlider: FC<ProductSummarySliderProps> = ({ data }) => {
  const { t } = useTranslation();

  const { productTableColumn: productTableColumnTranslations } = storeOverviewTranslations;

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const [selectedChip, setSelectedChip] = useState<'image' | 'details'>('image');

  useEffect(() => {
    if (!isMobile) {
      setSelectedChip('image');
    }
  }, [isMobile]);

  if (!data) return null;

  const {
    subIndexId,
    subIndexDescription,
    departmentName,
    departmentNumber,
    productId,
    productName,
    netSale,
    price,
    soldPieces,
    differenceLastLastWeek,
    rankingVsArea,
    rankingVsSalesMarket,
  } = data;

  const imageUrl = getArticleAssetUrl({
    articleId: `${data.productId}${data.articleIdentifier}`,
    assetType: 'descriptiveStillLife',
    rendition: 'medium',
  });

  return (
    <ColumnFlexContainer>
      <Typography type="button">{productName}</Typography>
      <SecondaryTextTypography type="caption">{productId}</SecondaryTextTypography>
      <Divider margin={[2, 0]} />
      {isMobile && (
        <RowFlexContainer gap={[2]} data-testid="product-summary-chips">
          <Chip
            key="productImage"
            label={t(productTableColumnTranslations.image.key, productTableColumnTranslations.image.defaultValue)}
            isSelected={selectedChip === 'image'}
            color="default"
            variant={selectedChip === 'image' ? 'filled' : 'outlined'}
            size="small"
            onClick={() => setSelectedChip('image')}
            borderRadius="4px"
          />
          <Chip
            key="productDetails"
            label={t(
              productTableColumnTranslations.details.key,
              productTableColumnTranslations.details.defaultValue,
            )}
            isSelected={selectedChip === 'details'}
            color="default"
            variant={selectedChip === 'details' ? 'filled' : 'outlined'}
            size="small"
            onClick={() => setSelectedChip('details')}
            borderRadius="4px"
          />
        </RowFlexContainer>
      )}

      {selectedChip === 'image' && (
        <RowFlexContainer margin={[2, 0, 0]}>
          <StyledProductImage
            data-testid="product-image"
            src={imageUrl}
            alt={productName}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = articlePlaceholder;
            }}
          />
          <ColumnFlexContainer margin={[0, 0, 0, 2]}>
            <Typography type="body4">{soldPieces}</Typography>
            <SecondaryTextTypography type="caption" gutterBottom>
              {t(soldProductsTranslations.piecesSold.key, soldProductsTranslations.piecesSold.defaultValue)}
            </SecondaryTextTypography>

            <Typography type="body4">{formatAmountWithSeparator(netSale, null, 0)}</Typography>
            <SecondaryTextTypography type="caption" gutterBottom>
              {t(soldProductsTranslations.salesNet.key, soldProductsTranslations.salesNet.defaultValue)}
            </SecondaryTextTypography>
          </ColumnFlexContainer>
        </RowFlexContainer>
      )}
      {selectedChip === 'details' && (
        <ColumnFlexContainer
          padding={[2]}
          data-testid="product-summary-slider"
          maxHeight="250px"
          overflow="scroll"
        >
          <MobileChipLayout
            layout="grid"
            gridColumns={2}
            layoutData={[
              {
                label: t(
                  productTableColumnTranslations.subIndex.key,
                  productTableColumnTranslations.subIndex.defaultValue,
                ),
                value: `${subIndexId} (${subIndexDescription})`,
                status: 'default',
              },
              {
                label: t(
                  productTableColumnTranslations.department.key,
                  productTableColumnTranslations.department.defaultValue,
                ),
                value: `${departmentNumber} (${departmentName})`,
                status: 'default',
              },
              {
                label: t(
                  productTableColumnTranslations.price.key,
                  productTableColumnTranslations.price.defaultValue,
                ),
                value: formatAmountWithSeparator(price, null, 2),
                status: 'default',
              },
              {
                label: t(
                  productTableColumnTranslations.netSales.key,
                  productTableColumnTranslations.netSales.defaultValue,
                ),
                value: formatAmountWithSeparator(netSale, null, 0),
                status: 'default',
              },
              {
                label: t(
                  productTableColumnTranslations.pieces.key,
                  productTableColumnTranslations.pieces.defaultValue,
                ),
                value: soldPieces.toString(),
                status: 'default',
              },
              {
                label: 'Diff LLW',
                value: getDifferenceText(differenceLastLastWeek, true),
                status: 'default',
              },
              {
                label: t(
                  productTableColumnTranslations.rankingVsArea.key,
                  productTableColumnTranslations.rankingVsArea.defaultValue,
                ),
                value: rankingVsArea.toString(),
                status: getRankStatus(rankingVsArea),
              },
              {
                label: t(
                  productTableColumnTranslations.rankingVsSalesMarket.key,
                  productTableColumnTranslations.rankingVsSalesMarket.defaultValue,
                ),
                value: rankingVsSalesMarket.toString(),
                status: getRankStatus(rankingVsSalesMarket),
              },
            ]}
          />
        </ColumnFlexContainer>
      )}
    </ColumnFlexContainer>
  );
};
